import { useState, useEffect } from "react";
import { useCurrency } from "../../context/CurrencyContext";
import { formatPriceSync } from "../../utils/currency";
import {
  fetchActivityOptions,
  fetchTimePricing,
  fetchTimeSlotsByOption,
  fetchVisitorTypes,
  calculatePrice,
  checkSlotCapacity,
  createBooking,
  createActivityOrder,
} from "../../lib/activityApi";
import axios from "axios";
import TimeSlotSelector from "./TimeSlotSelector";
import GuestSelector from "./GuestSelector";
import PriceSummary from "./PriceSummary";
import { useAuth } from "../../context/AuthContext";
import { FaTimes, FaCheck, FaChevronRight } from "react-icons/fa";

export default function BookingWidget({ activityId, activity, country, onBookingComplete }) {
  const { currency } = useCurrency();
  const { user } = useAuth();
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [slotsFromApi, setSlotsFromApi] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [visitorDetails, setVisitorDetails] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [detailsError, setDetailsError] = useState("");
  const [calculateError, setCalculateError] = useState(""); // inline error when Calculate clicked without package etc.
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null); // null = not loaded / not logged in; number = balance of whoever is booking (user or admin)
  const [walletLoading, setWalletLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [pickupLocation, setPickupLocation] = useState("");
  const [capacityFull, setCapacityFull] = useState(false);
  const BASE = (import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000");

  useEffect(() => {
    if (!activityId) return;
    fetchActivityOptions(activityId).then(setOptions);
    fetchVisitorTypes().then(setVisitorTypes);
  }, [activityId]);

  useEffect(() => {
    if (user?.name) setCustomerName(user.name);
    if (user?.email) setCustomerEmail(user.email);
  }, [user]);

  useEffect(() => {
    if (!selectedOption) return;
    fetchTimePricing(selectedOption._id).then((rules) => {
      const slots = new Set();
      rules.forEach((r) => {
        if (r.dayType === "allday") {
          ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00"].forEach((t) => slots.add(t));
        } else {
          const start = r.startTime || "09:00";
          const end = r.endTime || "17:00";
          for (let h = parseInt(start.slice(0, 2), 10); h <= parseInt(end.slice(0, 2), 10); h++) {
            slots.add(`${String(h).padStart(2, "0")}:00`);
          }
        }
      });
      const arr = Array.from(slots).sort();
      setTimeSlots(arr.length ? arr : ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"]);
      setSelectedTime(arr[0] || "10:00");
    });
    fetchTimeSlotsByOption(selectedOption._id).then((apiSlots) => {
      const list = Array.isArray(apiSlots) ? apiSlots : [];
      setSlotsFromApi(list);
      setSelectedSlot(null);
    }).catch(() => {
      setSlotsFromApi([]);
      setSelectedSlot(null);
    });
  }, [selectedOption]);

  useEffect(() => {
    if (!selectedOption || !visitorTypes.length) return;
    const allowedIds = selectedOption.allowedVisitorTypeIds || [];
    const typesToUse = allowedIds.length
      ? visitorTypes.filter((vt) => allowedIds.some((id) => String(id) === String(vt._id)))
      : visitorTypes;
    setVisitors(
      typesToUse.map((vt) => ({
        type: vt.name,
        typeId: vt._id,
        count: 0,
        multiplier: vt.priceMultiplier,
        minAge: vt.minAge,
        maxAge: vt.maxAge,
      }))
    );
  }, [selectedOption, visitorTypes]);

  const incrementVisitor = (typeId) => {
    const v = visitors.find((x) => x.typeId === typeId);
    if (!v || (selectedOption?.maxCapacity && visitors.reduce((s, x) => s + x.count, 0) >= selectedOption.maxCapacity)) return;
    setVisitors((prev) => prev.map((x) => (x.typeId === typeId ? { ...x, count: x.count + 1 } : x)));
    setPriceBreakdown(null);
  };

  const decrementVisitor = (typeId) => {
    setVisitors((prev) => prev.map((x) => (x.typeId === typeId && x.count > 0 ? { ...x, count: x.count - 1 } : x)));
    setPriceBreakdown(null);
  };

  // Keep visitorDetails in sync with visitor counts (one entry per guest)
  useEffect(() => {
    setVisitorDetails((prev) => {
      const next = { ...prev };
      visitors.forEach((v) => {
        const typeId = v.typeId;
        const count = v.count || 0;
        const existing = next[typeId] || [];
        if (count > existing.length) {
          const toAdd = count - existing.length;
          next[typeId] = [...existing, ...Array.from({ length: toAdd }, () => ({ name: "", phone: "", age: "" }))];
        } else if (count < existing.length) {
          next[typeId] = existing.slice(0, count);
        }
      });
      return next;
    });
  }, [visitors]);

  const setGuestDetail = (typeId, index, field, value) => {
    setVisitorDetails((prev) => {
      const arr = [...(prev[typeId] || [])];
      if (!arr[index]) arr[index] = { name: "", phone: "", age: "" };
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [typeId]: arr };
    });
  };

  const handleCalculatePrice = async () => {
    setCalculateError("");
    setCapacityFull(false);
    if (!selectedOption) {
      setCalculateError("Please select an option first.");
      return;
    }
    if (!selectedDate) {
      setCalculateError("Please select a date.");
      return;
    }
    const totalVisitors = visitors.reduce((s, v) => s + v.count, 0);
    if (totalVisitors === 0) {
      setCalculateError("Please add at least one guest.");
      return;
    }
    setLoading(true);
    try {
      const useSlotBased = Array.isArray(slotsFromApi) && slotsFromApi.length > 0;
      if (useSlotBased && !selectedSlot?._id) {
        setCalculateError("Please select a time slot.");
        setLoading(false);
        return;
      }
      const bookingTime = useSlotBased ? selectedSlot.startTime : selectedTime;
      const selectedDateTime = new Date(`${selectedDate}T${bookingTime}:00`);
      const cap = useSlotBased
        ? await checkSlotCapacity({ slotId: selectedSlot._id })
        : await checkSlotCapacity(selectedOption._id, selectedDateTime.toISOString());
      if (cap?.isFull) {
        setCapacityFull(true);
        setCalculateError("This option is full for the selected date and time. Please choose another date or time.");
        setPriceBreakdown(null);
        setLoading(false);
        return;
      }
      const result = await calculatePrice({
        activityOptionId: selectedOption._id,
        visitors: visitors.filter((v) => v.count > 0).map((v) => ({ type: v.type, count: v.count })),
        selectedDateTime: selectedDateTime.toISOString(),
        selectedDate,
        selectedTime: bookingTime,
      });
      setPriceBreakdown(result);
    } catch (e) {
      alert("Could not calculate price. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const openDetailsModal = () => {
    setDetailsError("");
    setShowDetailsModal(true);
  };

  /** Build booking payload from current state (used for both wallet and card payment). */
  const getBookingPayload = (paymentMethod) => {
    const visitorsWithDetails = visitors
      .filter((v) => v.count > 0)
      .map((v) => {
        const typeDetails = visitorDetails[v.typeId] || [];
        return {
          type: v.type,
          typeId: v.typeId,
          count: v.count,
          details: typeDetails.slice(0, v.count).map((d) => ({
            name: (d.name || "").trim(),
            phone: (d.phone || "").trim(),
            age: parseInt(d.age, 10) || 0,
          })),
        };
      });
    const useSlotBased = Array.isArray(slotsFromApi) && slotsFromApi.length > 0 && selectedSlot?._id;
    const bookingTime = useSlotBased ? selectedSlot.startTime : selectedTime;
    return {
      activityOptionId: selectedOption._id,
      visitors: visitorsWithDetails,
      selectedDateTime: new Date(`${selectedDate}T${bookingTime}:00`).toISOString(),
      slotId: useSlotBased ? selectedSlot._id : undefined,
      selectedDate: useSlotBased ? selectedDate : undefined,
      totalPrice: priceBreakdown?.total || 0,
      currency: priceBreakdown?.currency || country?.currency || currency?.code || "USD",
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      customerPhone: customerPhone.trim(),
      pickupLocation: (selectedOption?.includesTransfers && pickupLocation) ? pickupLocation.trim() : "",
      paymentMethod: paymentMethod || "card",
      priceBreakdown,
    };
  };

  const handleBook = (details) => {
    if (!priceBreakdown || !selectedOption || !selectedDate) return;
    const totalVisitors = visitors.reduce((s, v) => s + v.count, 0);
    if (totalVisitors === 0) return;

    const name = (details?.customerName ?? customerName)?.trim() || "";
    const email = (details?.customerEmail ?? customerEmail)?.trim() || "";
    if (!name || !email) {
      setDetailsError("Name and email are required.");
      return;
    }

    const visitorsWithDetails = visitors
      .filter((v) => v.count > 0)
      .map((v) => {
        const typeDetails = visitorDetails[v.typeId] || [];
        return {
          type: v.type,
          typeId: v.typeId,
          count: v.count,
          details: typeDetails.slice(0, v.count).map((d) => ({
            name: (d.name || "").trim(),
            phone: (d.phone || "").trim(),
            age: parseInt(d.age, 10) || 0,
          })),
        };
      });
    const allGuestsHaveNames = visitorsWithDetails.every((v) =>
      v.details.every((d) => (d.name || "").trim().length > 0)
    );
    if (!allGuestsHaveNames) {
      setDetailsError("Please enter the name of each guest (Adult/Child).");
      return;
    }

    setDetailsError("");
    setShowDetailsModal(false);
    setShowReviewModal(true);
  };

  // Jo booking kar raha hai (user ya admin), usi ka wallet use hota hai — backend req.user se balance deta hai
  useEffect(() => {
    if (!showReviewModal) return;
    setWalletBalance(null);
    setWalletLoading(true);
    let cancelled = false;
    axios.get(`${BASE}/api/wallet/my-balance`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        const total = Array.isArray(data) && data[0] != null ? Number(data[0].total) || 0 : 0;
        setWalletBalance(total);
      })
      .catch(() => {
        if (!cancelled) setWalletBalance(null); // 401 / error = not logged in
      })
      .finally(() => {
        if (!cancelled) setWalletLoading(false);
      });
    return () => { cancelled = true; };
  }, [showReviewModal, BASE]);

  const handlePayWithWallet = async () => {
    if (walletBalance == null) {
      setDetailsError("Login to pay with wallet.");
      return;
    }
    const total = Number(priceBreakdown?.total) || 0;
    if (walletBalance < total) {
      setDetailsError("Insufficient wallet balance.");
      return;
    }
    setDetailsError("");
    setPaymentLoading(true);
    try {
      const res = await createBooking(getBookingPayload("wallet"));
      if (res?.success) {
        setShowReviewModal(false);
        onBookingComplete?.(res.data);
      } else {
        throw new Error(res?.error || "Booking failed");
      }
    } catch (e) {
      setDetailsError(e.response?.data?.error || e.message || "Booking failed. Try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePayWithRazorpay = async () => {
    setDetailsError("");
    setPaymentLoading(true);
    try {
      const data = await createActivityOrder(priceBreakdown?.total || 0);
      if (!data?.order) throw new Error("Could not create order");

      const options = {
        key: import.meta.env.VITE_KEYID,
        amount: data.order.amount,
        currency: "INR",
        name: "Celestial Tours",
        description: "Activity Booking",
        order_id: data.order.id,
        handler: async function () {
          try {
            const res = await createBooking(getBookingPayload("card"));
            if (res?.success) {
              setShowReviewModal(false);
              onBookingComplete?.(res.data);
            } else throw new Error(res?.error || "Booking failed");
          } catch (err) {
            setDetailsError(err.response?.data?.error || err.message || "Booking failed");
          } finally {
            setPaymentLoading(false);
          }
        },
        prefill: { name: customerName, email: customerEmail, contact: customerPhone || "" },
        theme: { color: "#1ABCE3" },
      };
      if (!window.Razorpay) {
        setDetailsError("Payment system not available. Please try again.");
        setPaymentLoading(false);
        return;
      }
      const razor = new window.Razorpay(options);
      razor.on("payment.failed", () => {
        setDetailsError("Payment failed. Please try again.");
        setPaymentLoading(false);
      });
      razor.on("modal.closed", () => setPaymentLoading(false));
      razor.open();
    } catch (e) {
      setDetailsError(e.response?.data?.error || e.message || "Could not start payment");
      setPaymentLoading(false);
    }
  };

  const submitDetailsForm = (e) => {
    e.preventDefault();
    handleBook({ customerName: customerName.trim(), customerEmail: customerEmail.trim(), customerPhone: customerPhone.trim() });
  };

  const canCalculate = selectedOption && selectedDate && visitors.some((v) => v.count > 0);
  useEffect(() => {
    if (canCalculate) setCalculateError("");
  }, [canCalculate]);

  const missingForCalculate = !selectedOption
    ? "Select a package"
    : !selectedDate
    ? "Select a date"
    : visitors.every((v) => v.count === 0)
    ? "Add at least one guest"
    : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 sticky top-6">
      <h3 className="text-lg font-bold text-[#222222] mb-1">Book this activity</h3>
      <p className="text-sm text-gray-500 mb-6">Follow the steps below. Select a package first, then date and guests.</p>

      {/* Step 1: Package (required) */}
      <div className={`mb-5 pb-5 border-b border-gray-100 ${calculateError === "Please select a package first." ? "ring-2 ring-red-200 rounded-xl p-3 -m-1 bg-red-50/50" : ""}`}>
        <div className="flex items-center gap-2 mb-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white bg-[#1ABCE3]">1</span>
          <label className="text-sm font-semibold text-gray-800">Choose a package <span className="text-red-500">*</span></label>
          {selectedOption && <span className="text-xs text-green-600 font-medium flex items-center gap-1"><FaCheck className="w-3 h-3" /> Selected</span>}
        </div>
        {!selectedOption && options.length > 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">Select one option below to continue. Package is required.</p>
        )}
        {options.length === 0 && <p className="text-sm text-gray-500">Loading options...</p>}
        <div className="space-y-2">
          {options.map((opt) => {
            const isSelected = selectedOption?._id === opt._id;
            const withTransfers = opt.includesTransfers === true;
            return (
              <button
                key={opt._id}
                type="button"
                onClick={() => { setSelectedOption(opt); setPriceBreakdown(null); setCalculateError(""); setCapacityFull(false); setPickupLocation(""); }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex justify-between items-center gap-3 ${
                  isSelected
                    ? "border-[#1ABCE3] bg-[#1ABCE3]/10 shadow-sm"
                    : "border-gray-200 hover:border-[#1ABCE3]/50 hover:bg-gray-50"
                }`}
              >
                <span className="flex flex-col items-start gap-0.5">
                  <span className="font-medium text-gray-900">{opt.name}</span>
                  <span className="text-xs text-gray-500">
                    {withTransfers ? "With transfers" : "Without transfers"}
                    {opt.maxCapacity != null ? ` · Max capacity: ${opt.maxCapacity}` : ""}
                    {opt.duration != null ? ` · ${opt.duration} min` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-[#222222]">{formatPriceSync(Number(opt.basePrice || 0), currency)}</span>
                  {isSelected ? <FaCheck className="w-4 h-4 text-[#1ABCE3]" /> : <FaChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                </span>
              </button>
            );
          })}
        </div>
        {selectedOption && (
          <div className="mt-3 p-3 rounded-xl bg-[#1ABCE3]/5 border border-[#1ABCE3]/20 text-sm">
            <div className="font-medium text-gray-800 mb-1">Selected: {selectedOption.name}</div>
            <div className="text-gray-600">
              {selectedOption.includesTransfers ? "With transfers" : "Without transfers"}
              {selectedOption.maxCapacity != null && ` · Capacity: ${selectedOption.maxCapacity} guests`}
              {selectedOption.duration != null && ` · Duration: ${selectedOption.duration} min`}
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Date & time */}
      <div className="mb-5 pb-5 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white bg-[#1ABCE3]">2</span>
          <label className="text-sm font-semibold text-gray-800">Date & time <span className="text-red-500">*</span></label>
        </div>
        {!selectedOption && <p className="text-xs text-gray-500 mb-2">Select a package above first.</p>}
        <TimeSlotSelector
          selectedDate={selectedDate}
          setSelectedDate={(d) => { setSelectedDate(d); setCalculateError(""); setCapacityFull(false); setPriceBreakdown(null); }}
          selectedTime={selectedTime}
          setSelectedTime={(t) => { setSelectedTime(t); setCapacityFull(false); setPriceBreakdown(null); }}
          timeSlots={timeSlots}
          slotsFromApi={slotsFromApi}
          selectedSlot={selectedSlot}
          setSelectedSlot={(s) => { setSelectedSlot(s); setCapacityFull(false); setPriceBreakdown(null); }}
          minDate={(() => {
            const toYMD = (d) => {
              if (!d) return undefined;
              const x = new Date(d);
              return isNaN(x.getTime()) ? undefined : x.toISOString().slice(0, 10);
            };
            let baseMinDate = toYMD(selectedOption?.bookingStartDate) || new Date().toISOString().slice(0, 10);
            if (selectedOption?.advanceBookingDays) {
              const advancedDate = new Date();
              advancedDate.setDate(advancedDate.getDate() + Number(selectedOption.advanceBookingDays));
              const advMinDate = advancedDate.toISOString().slice(0, 10);
              return advMinDate > baseMinDate ? advMinDate : baseMinDate;
            }
            return baseMinDate;
          })()}
          maxDate={(() => {
            const toYMD = (d) => {
              if (!d) return undefined;
              const x = new Date(d);
              return isNaN(x.getTime()) ? undefined : x.toISOString().slice(0, 10);
            };
            return toYMD(selectedOption?.bookingEndDate) || undefined;
          })()}
        />
      </div>

      {/* Step 3: Guests */}
      <div className="mb-5 pb-5 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white bg-[#1ABCE3]">3</span>
          <label className="text-sm font-semibold text-gray-800">Number of guests <span className="text-red-500">*</span></label>
        </div>
        {!selectedOption && <p className="text-xs text-gray-500 mb-2">Select a package first to see guest options.</p>}
        <GuestSelector
          visitors={visitors}
          incrementVisitor={incrementVisitor}
          decrementVisitor={decrementVisitor}
          maxCapacity={selectedOption?.maxCapacity || 99}
        />
      </div>

      {/* Step 4: Calculate price */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white bg-[#1ABCE3]">4</span>
          <label className="text-sm font-semibold text-gray-800">See price & book</label>
        </div>
        {missingForCalculate && !canCalculate && (
          <p className="text-xs text-gray-500 mb-2">Complete steps above: {missingForCalculate.toLowerCase()}.</p>
        )}
        {capacityFull && <p className="text-sm text-red-600 mb-2 font-medium">This option is full for the selected date and time. Please choose another date or time.</p>}
        {calculateError && !capacityFull && <p className="text-sm text-red-600 mb-2">{calculateError}</p>}
        <button
          type="button"
          onClick={handleCalculatePrice}
          disabled={loading || !canCalculate || capacityFull}
          className="w-full py-3 rounded-xl font-semibold text-white bg-green-400 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed mb-1"
        >
          {loading ? "Calculating..." : canCalculate ? "Calculate price" : `Calculate price (${missingForCalculate})`}
        </button>
        {!canCalculate && <p className="text-xs text-gray-500 text-center">Select package, date and add guests to enable.</p>}
      </div>

      {priceBreakdown && (
        <PriceSummary
          priceBreakdown={priceBreakdown}
          currency={currency}
          onBook={openDetailsModal}
          loading={bookingLoading}
        />
      )}

      {showDetailsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !bookingLoading && setShowDetailsModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#222222]">Booking details</h3>
              <button type="button" onClick={() => !bookingLoading && setShowDetailsModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Primary contact and each guest’s name are required. Admin will see who is in the booking.</p>
            <form onSubmit={submitDetailsForm} className="space-y-4">
              <div className="font-medium text-gray-800">Primary contact</div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your full name *</label>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Your name" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1ABCE3] focus:border-transparent" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1ABCE3] focus:border-transparent" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+91 98765 43210" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1ABCE3] focus:border-transparent" />
              </div>

              {selectedOption?.includesTransfers && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup location <span className="text-gray-500 font-normal">(this option includes transfers)</span></label>
                  <input type="text" value={pickupLocation} onChange={(e) => setPickupLocation(e.target.value)} placeholder="Hotel name, address or landmark" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1ABCE3] focus:border-transparent" />
                </div>
              )}

              {visitors.filter((v) => v.count > 0).length > 0 && (
                <div className="pt-3 border-t border-gray-200">
                  <div className="font-medium text-gray-800 mb-3">Guest names (who is attending)</div>
                  {visitors.filter((v) => v.count > 0).map((v) => (
                    <div key={v.typeId} className="space-y-2 mb-4 last:mb-0">
                      {Array.from({ length: v.count }).map((_, i) => (
                        <div key={`${v.typeId}-${i}`} className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-gray-50 rounded-xl">
                          <div className="sm:col-span-3 text-xs font-medium text-gray-600 mb-1">{v.type} – Person {i + 1}</div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Name *</label>
                            <input type="text" value={(visitorDetails[v.typeId] || [])[i]?.name || ""} onChange={(e) => setGuestDetail(v.typeId, i, "name", e.target.value)} placeholder="Full name" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#1ABCE3]" required />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Age</label>
                            <input type="number" min="0" max="120" value={(visitorDetails[v.typeId] || [])[i]?.age ?? ""} onChange={(e) => setGuestDetail(v.typeId, i, "age", e.target.value)} placeholder="Age" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#1ABCE3]" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-0.5">Phone</label>
                            <input type="tel" value={(visitorDetails[v.typeId] || [])[i]?.phone || ""} onChange={(e) => setGuestDetail(v.typeId, i, "phone", e.target.value)} placeholder="Phone" className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#1ABCE3]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {detailsError && <p className="text-sm text-red-600">{detailsError}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowDetailsModal(false)} disabled={bookingLoading} className="flex-1 py-3 rounded-xl font-semibold border-2 border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={bookingLoading} className="flex-1 py-3 rounded-xl font-semibold text-white hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: "#1ABCE3" }}>{bookingLoading ? "Please wait..." : "Proceed to payment"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review & Payment modal */}
      {showReviewModal && priceBreakdown && selectedOption && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !paymentLoading && setShowReviewModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#222222]">Review & Pay</h3>
              <button type="button" onClick={() => !paymentLoading && setShowReviewModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-sm">
              <div className="p-3 bg-gray-50 rounded-xl">
                <div className="font-semibold text-gray-800 mb-1">{activity?.name}</div>
                <div className="text-gray-600">{selectedOption?.name}</div>
                <div className="text-gray-500 mt-1">{selectedDate} · {selectedTime}</div>
              </div>

              <div className="p-3 bg-gray-50 rounded-xl">
                <div className="font-medium text-gray-800 mb-2">Primary contact</div>
                <div className="text-gray-700">{customerName}</div>
                <div className="text-gray-600">{customerEmail}</div>
                {customerPhone && <div className="text-gray-600">{customerPhone}</div>}
              </div>

              {pickupLocation && selectedOption?.includesTransfers && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <div className="font-medium text-gray-800 mb-1">Pickup location</div>
                  <div className="text-gray-700">{pickupLocation}</div>
                </div>
              )}

              <div className="p-3 bg-gray-50 rounded-xl">
                <div className="font-medium text-gray-800 mb-2">Visitors</div>
                {visitors.filter((v) => v.count > 0).map((v) => (
                  <div key={v.typeId} className="mb-2 last:mb-0">
                    {Array.from({ length: v.count }).map((_, i) => {
                      const d = (visitorDetails[v.typeId] || [])[i] || {};
                      return (
                        <div key={`${v.typeId}-${i}`} className="flex flex-wrap gap-x-2 gap-y-0.5 text-gray-700">
                          <span className="font-medium">{v.type} {i + 1}:</span>
                          <span>{d.name || "—"}</span>
                          {(d.age != null && d.age !== "") && <span>(Age {d.age})</span>}
                          {d.phone && <span>· {d.phone}</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              <div className="p-3 border border-gray-200 rounded-xl">
                <div className="font-medium text-gray-800 mb-2">Price breakdown</div>
                {(priceBreakdown.breakdown || []).map((row, idx) => (
                  <div key={idx} className="flex justify-between items-baseline py-1 text-gray-700">
                    <span>
                      {row.type} × {row.count}
                      {(row.timeMultiplier != null && row.timeMultiplier !== 1) || (row.visitorMultiplier != null && row.visitorMultiplier !== 1) ? (
                        <span className="text-gray-500 text-xs ml-1">
                          (×{[row.timeMultiplier, row.visitorMultiplier].filter(Boolean).join(" × ")})
                        </span>
                      ) : null}
                      {row.pricePerPerson != null ? ` @ ${priceBreakdown.currencySymbol || ""} ${Number(row.pricePerPerson).toFixed(2)}` : ""}
                    </span>
                    <span className="font-medium">{priceBreakdown.currencySymbol || ""} {Number(row.subtotal || 0).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-gray-200 font-bold text-[#222222]">
                  <span>Total</span>
                  <span>{priceBreakdown.currencySymbol || ""} {Number(priceBreakdown.total || 0).toFixed(2)}</span>
                </div>
              </div>

              {detailsError && <p className="text-sm text-red-600">{detailsError}</p>}

              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="button"
                  onClick={handlePayWithWallet}
                  disabled={paymentLoading || walletLoading || walletBalance == null || (walletBalance != null && walletBalance < (priceBreakdown?.total || 0))}
                  className="w-full py-3 rounded-xl font-semibold border-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: "#1ABCE3", color: "#1ABCE3", backgroundColor: "rgba(26,188,227,0.08)" }}
                >
                  {walletLoading ? "Loading wallet…" : walletBalance == null ? "Login to pay with wallet" : walletBalance < (priceBreakdown?.total || 0) ? `Insufficient balance (${priceBreakdown?.currencySymbol || ""} ${Number(walletBalance || 0).toFixed(2)} available)` : "Pay with Wallet"}
                </button>
                <button
                  type="button"
                  onClick={handlePayWithRazorpay}
                  disabled={paymentLoading}
                  className="w-full py-3 rounded-xl font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "#1ABCE3" }}
                >
                  {paymentLoading ? "Please wait…" : "Pay with Razorpay (Card / UPI)"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowReviewModal(false); setShowDetailsModal(true); setDetailsError(""); }}
                  disabled={paymentLoading}
                  className="w-full py-2 rounded-xl font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  Back to details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
